// Full-page detail for a DB storage (postgres/mongo) — the file-source page's
// analogue for databases. Same chrome as DataSourceView (back button, big header,
// stacked section cards) but driven by the unified storage endpoints. Sections are
// role-aware: a READ source (data_source db) gets Connection · Analytics · Mappings
// (field→element, with Generate) · Schema · Records; a WRITE target (data_target)
// gets Connection · Mappings (element→table/column) · Schema · Records.
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPageUrl } from "@/utils";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Database, Server, Loader2, BarChart3, RefreshCw, Wand2, Inbox, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { fetchData } from "@/fetchapi/fetchapi";
import { runEndpoint } from "@/services/flow";
import {
  ConnectionTab,
  StorageMappingsTab,
  SchemaView,
  RecordsTab,
} from "./Pipeline";
import { getSourceSchema, getTargetSchema } from "@/services/pipeline";
import { getKindFlags, loadSourceKindFlags } from "@/services/sourceKinds";
import { storageType as storageTypeMeta } from "@/components/pipeline/PipelineCanvas";
import UnstructuredItemsPanel from "@/components/pipeline/UnstructuredItemsPanel";

// Title-case a storage type for the header label (storageType() labels are lowercase).
const titleCase = (s) => (s || "").charAt(0).toUpperCase() + (s || "").slice(1);
// The per-kind capability sets that used to live here (UNSTRUCTURED,
// ENGINE_READABLE, AGENTIC, DB_PROFILABLE) now come from the source_kind
// registry via @/services/sourceKinds — which keeps these exact sets as its
// fallback for the first paint and for legacy kinds the registry doesn't list.

const STATUS_PILL = {
  Active: "bg-green-100 text-green-800",
  Draft: "bg-indigo-100 text-indigo-800",
  Maintenance: "bg-amber-100 text-amber-800",
  Deprecated: "bg-red-100 text-red-800",
  Inactive: "bg-gray-200 text-gray-700",
};

function SectionCard({ icon: Icon, color, title, actions, children }) {
  return (
    <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon size={18} color={color || "#377AD6"} />}
          <h2 className="text-base font-medium" style={{ color: "var(--page-text)" }}>{title}</h2>
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

export default function StorageDetail() {
  // Route: /storages/:origin/:id  (origin = 'data_source' | 'data_target')
  const { origin, id } = useParams();
  const navigate = useNavigate();
  const isTarget = origin === "data_target";

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  // Schema (live discover) shared by the Schema + Records sections.
  const [schemaData, setSchemaData] = useState(null);
  const [schemaErr, setSchemaErr] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  // Bumped when the source_kind catalog resolves, so the capability flags below
  // (read synchronously, fallback-first) re-evaluate against the registry.
  const [kindFlagsVersion, setKindFlagsVersion] = useState(0);
  useEffect(() => {
    let alive = true;
    loadSourceKindFlags().then(() => { if (alive) setKindFlagsVersion((v) => v + 1); });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tbl = isTarget ? "data_target" : "data_source";
      const d = await fetchData(`${tbl}?id=${id}`);
      setRow(d?.data ? (Array.isArray(d.data) ? d.data[0] : d.data) : d);
    } catch (e) {
      toast.error("Failed to load storage");
    } finally {
      setLoading(false);
    }
  }, [isTarget, id]);
  useEffect(() => { load(); }, [load]);

  const loadSchema = useCallback(async () => {
    setSchemaLoading(true); setSchemaErr(null);
    try {
      const data = isTarget ? await getTargetSchema(id) : await getSourceSchema(id);
      setSchemaData(data);
    } catch (e) {
      setSchemaErr(e?.message || "Failed to load schema");
    } finally {
      setSchemaLoading(false);
    }
  }, [isTarget, id]);

  const kind = (row?.kind || row?.source_type || "storage").toLowerCase();
  const tmeta = storageTypeMeta(kind); // {label,color,icon} for all 10 types
  const meta = { label: titleCase(kind), icon: tmeta.icon, color: tmeta.color };
  const Icon = meta.icon;
  const name = row?.name || row?.source_name || `storage ${id}`;
  // Capability flags from the source_kind registry. getKindFlags is synchronous
  // and falls back to the old hardcoded sets, so the first paint is never blank;
  // kindFlagsVersion re-runs this once the catalog resolves.
  const flags = useMemo(() => getKindFlags(kind), [kind, kindFlagsVersion]);
  const engineReadable = flags.engine_readable;
  const isUnstructured = !isTarget && !flags.is_structured;

  // Generate column profiling (analytics) for a READ db source, then refresh.
  const generateAnalysis = async () => {
    setAnalyzing(true);
    try {
      await fetchData(`data_source/${id}/analyze_db`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      toast.success("Analysis generated");
      await load();
      await loadSchema();
    } catch (e) {
      toast.error(`Analysis failed: ${e?.message || e}`);
    } finally {
      setAnalyzing(false);
    }
  };

  // Generate source field mappings (read direction) from the analysis.
  const generateMappings = async () => {
    try {
      await runEndpoint("cortex.vdd.load.data_source_mappings", { dataSourceId: Number(id) });
      toast.success("Field mappings generated");
      window.dispatchEvent(new CustomEvent("cortex:data-changed"));
    } catch (e) {
      toast.error(`Mapping generation failed: ${e?.message || e}`);
    }
  };

  const shell = "pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5";
  if (loading) {
    return <div className={shell}><div className="text-page-muted text-sm"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</div></div>;
  }

  const analysis = (() => { let a = row?.analysis; if (typeof a === "string") { try { a = JSON.parse(a); } catch { a = null; } } return a; })();
  const analysisTables = analysis?.tables || [];

  return (
    <div className={shell}>
      <button onClick={() => navigate(createPageUrl("data-sources"))}
        className="flex items-center gap-1 w-fit text-sm text-nav-active cursor-pointer bg-transparent border-0 p-0 -mb-2 hover:text-nav-active-hover transition-colors">
        <ArrowLeft size={16} /> Back to Data Sources
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-9 h-9 rounded-[6px] flex items-center justify-center" style={{ background: meta.color + "20", color: meta.color }}>
              <Icon size={20} />
            </div>
            <h1 className="text-2xl md:text-[32px] font-medium leading-tight text-page-title break-words">{name}</h1>
            <span className={`inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap ${STATUS_PILL[row?.status] || "bg-gray-200 text-gray-700"}`}>
              {row?.status || "Active"}
            </span>
            {flags.is_agentic && (
              <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap"
                style={{ background: "rgba(139,92,246,0.15)", color: "rgba(139,92,246,0.95)" }}>
                <Sparkles size={11} /> AI-built source
              </span>
            )}
          </div>
          <p className="text-sm font-normal text-page-muted font-mono">{meta.label} · {kind}:{id} · {isTarget ? "write storage" : "read source"}</p>
          {row?.description && <p className="text-sm font-normal text-page-muted">{row.description}</p>}
        </div>
      </div>

      {/* Connection */}
      <SectionCard icon={Database} color={meta.color} title="Connection">
        <ConnectionTab origin={origin} entityId={id} storageType={kind} />
      </SectionCard>

      {/* Per-item extraction — UNSTRUCTURED sources (slack/gmail). Replaces the
          static Analytics/Schema sections: there is no one schema for the whole
          source; each message/email is mapped & value-extracted individually,
          with a browsable history. */}
      {isUnstructured && (
        <SectionCard icon={Inbox} color={meta.color} title="Messages & extracted values">
          <UnstructuredItemsPanel sourceId={Number(id)} color={meta.color} />
        </SectionCard>
      )}

      {/* Analytics — read sources only, and only kinds the DB column-profiler
          (generate_db_analysis) supports. Agentic kinds (api/scraper/enrichment/
          rss) get their schema from the master/spec — profiling them via the DB
          analyzer would just error. Skipped for unstructured kinds too. */}
      {!isTarget && engineReadable && !isUnstructured && flags.db_profilable && (
        <SectionCard icon={BarChart3} color={meta.color} title="Analytics"
          actions={
            <button onClick={generateAnalysis} disabled={analyzing}
              className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer border-0 hover:bg-nav-active-dark transition-colors disabled:opacity-50">
              {analyzing ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />}
              {analyzing ? "Analyzing…" : "Generate analysis"}
            </button>
          }>
          {analysisTables.length === 0 ? (
            <div className="text-page-muted text-sm">No analysis yet. Click “Generate analysis” to profile this source's columns (type, null/distinct counts).</div>
          ) : (
            <div className="space-y-4">
              {analysisTables.map((t) => (
                <div key={t.name}>
                  <div className="text-sm font-medium mb-1">{t.name}</div>
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-page-muted border-b border-page-divider"><th className="py-1">Column</th><th>Type</th><th>Count</th><th>Null</th><th>Distinct</th></tr></thead>
                    <tbody>
                      {(t.columns || []).map((c) => (
                        <tr key={c.name} className="border-b border-page-divider/50">
                          <td className="py-1 font-mono text-xs">{c.name}</td>
                          <td className="text-xs">{c.type}</td>
                          <td className="text-xs">{c.stats?.count ?? "—"}</td>
                          <td className="text-xs">{c.stats?.null ?? "—"}</td>
                          <td className="text-xs">{c.stats?.distinct ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* Mappings — unified surface; read sources get a Generate button.
          Hidden for unstructured kinds: mapping is per-item (dynamic), shown in
          the Messages panel above, not as one static source-field mapping. */}
      {!isUnstructured && (
      <SectionCard icon={Wand2} color={meta.color} title={isTarget ? "Mappings (write)" : "Source field mappings"}
        actions={!isTarget && (
          <button onClick={generateMappings} disabled={analysisTables.length === 0}
            title={analysisTables.length === 0 ? "Generate analysis first" : "Suggest column → data element mappings from the analysis"}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active text-sm font-medium cursor-pointer bg-card-bg hover:border-nav-active-hover transition-colors disabled:opacity-50">
            <Wand2 size={15} /> Generate mappings
          </button>
        )}>
        <StorageMappingsTab storageId={Number(id)} storageType={kind} writeDirection={isTarget} />
      </SectionCard>
      )}

      {/* Schema — hidden for unstructured (no single schema for the source). */}
      {!isUnstructured && (
      <SectionCard icon={Database} color={meta.color} title="Schema"
        actions={engineReadable && (
          <button onClick={loadSchema} disabled={schemaLoading}
            className="flex items-center gap-2 h-9 px-3 rounded-[5px] border border-page-divider text-sm cursor-pointer bg-card-bg hover:border-nav-active transition-colors disabled:opacity-50">
            {schemaLoading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} {schemaData ? "Refresh" : "Discover"}
          </button>
        )}>
        {!engineReadable ? (
          <div className="text-page-muted text-sm">Live schema discovery for <span className="font-mono">{kind}</span> sources isn't available yet — connection + mappings are configurable here, and the engine driver for {kind} can be added later.</div>
        ) : schemaLoading ? (
          <div className="text-page-muted text-sm"><Loader2 className="inline animate-spin mr-2" size={14} />Discovering schema…</div>
        ) : schemaErr ? (
          <div className="text-red-700 text-sm">{schemaErr}</div>
        ) : !schemaData?.tables?.length ? (
          <div className="text-page-muted text-sm">Click “Discover” to read the live schema.</div>
        ) : (
          <SchemaView schemaData={schemaData} isSource={!isTarget} />
        )}
      </SectionCard>
      )}

      {/* Records — for unstructured kinds this serves the pivoted extraction
          rows (element columns), i.e. exactly what the pipeline would consume. */}
      <SectionCard icon={Server} color={meta.color} title={isUnstructured ? "Extracted records (pipeline view)" : "Records"}>
        {!engineReadable ? (
          <div className="text-page-muted text-sm">Live record preview for <span className="font-mono">{kind}</span> sources isn't available yet (no engine driver).</div>
        ) : (
          <RecordsTab kind={isTarget ? "target" : "source"} entityId={id} schemaData={schemaData} loadSchema={loadSchema} schemaLoading={schemaLoading} schemaErr={schemaErr} />
        )}
      </SectionCard>
    </div>
  );
}
